import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  Documentable,
  Job,
  JobDetail,
  JobReport,
  AgendaSchedule,
  AgendaQuery,
  CreateJobRequest,
  EditJobRequest,
  AssignTechniciansRequest,
  CancelJobRequest,
  RescheduleJobRequest,
  DeleteJobQuery,
  RepeatSchedule,
  RepeatScheduleRequest,
  JobReportRequest,
  ReportCatalogResponse,
  SendDocumentRequest,
  JobListQuery,
  PdfLink,
  AgendaResizeJobRequest,
  AgendaScheduleJobRequest,
  TechnicianSuggestion,
  TechnicianSuggestionQuery,
  JobChangesResponse,
} from '@/types'

// Booleans must serialise as 1/0 (strict query-param validation).
function suggestionParams(
  q: TechnicianSuggestionQuery
): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  if (q.quote_id) params.quote_id = q.quote_id
  if (q.contract_id) params.contract_id = q.contract_id
  if (q.service_type) params.service_type = q.service_type
  if (q.intervention_type) params.intervention_type = q.intervention_type
  if (q.scheduled_date) params.scheduled_date = q.scheduled_date
  if (q.start_time) params.start_time = q.start_time
  if (q.end_time) params.end_time = q.end_time
  if (typeof q.latitude === 'number') params.latitude = q.latitude
  if (typeof q.longitude === 'number') params.longitude = q.longitude
  if (q.priority) params.priority = q.priority
  if (q.limit) params.limit = q.limit
  if (q.search) params.search = q.search
  if (q.technician_ids?.length) params.technician_ids = q.technician_ids
  if (q.exclude_job_id) params.exclude_job_id = q.exclude_job_id
  return params
}

// 1:1 with Schema_Specification Section 4.4 (jobs / agenda / reports).
export const jobsApi = {
  agenda: (q: AgendaQuery = {}) =>
    apiClient
      .get<ApiResponse<AgendaSchedule>>('/agenda', { params: q })
      .then(r => r.data),

  changes: (cursor?: string | null) =>
    apiClient
      .get<ApiResponse<JobChangesResponse>>('/jobs/changes', {
        params: cursor ? { cursor } : undefined,
      })
      .then(r => r.data),

  unassigned: (q: JobListQuery = {}) =>
    apiClient
      .get<ApiList<Job>>('/agenda/unassigned', {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  list: (q: JobListQuery = {}) =>
    apiClient
      .get<ApiList<Job>>('/jobs', { params: withListDefaults(q) })
      .then(r => r.data),

  get: (id: string) =>
    apiClient.get<ApiResponse<JobDetail>>(`/jobs/${id}`).then(r => r.data),

  create: (body: CreateJobRequest) =>
    apiClient.post<ApiResponse<Job>>('/jobs', body).then(r => r.data),

  // Feedback #14 - ranked technician suggestions for assignment.
  technicianSuggestions: (q: TechnicianSuggestionQuery) =>
    apiClient
      .get<ApiResponse<TechnicianSuggestion[]>>(
        '/jobs/technician-suggestions',
        {
          params: suggestionParams(q),
        }
      )
      .then(r => r.data),

  // Feedback #13 - operator job attachments: upload a new file or link an
  // existing client/library file to the job.
  uploadFile: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return apiClient
      .post<ApiResponse<Documentable>>(`/jobs/${id}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  attachFile: (
    id: string,
    fileId: string,
    docType?: string,
    activityAction: 'attached' | 'copied' = 'attached'
  ) =>
    apiClient
      .post<ApiResponse<Documentable>>(`/jobs/${id}/files/attach`, {
        file_id: fileId,
        doc_type: docType,
        activity_action: activityAction,
      })
      .then(r => r.data),

  update: (id: string, body: EditJobRequest) =>
    apiClient.put<ApiResponse<Job>>(`/jobs/${id}`, body).then(r => r.data),

  updateAgendaTime: (id: string, body: AgendaResizeJobRequest) =>
    apiClient
      .patch<ApiResponse<Job>>(`/agenda/jobs/${id}/time`, body)
      .then(r => r.data),

  updateAgendaSchedule: (id: string, body: AgendaScheduleJobRequest) =>
    apiClient
      .patch<ApiResponse<Job>>(`/agenda/jobs/${id}/schedule`, body)
      .then(r => r.data),

  remove: (id: string, q: DeleteJobQuery = {}) =>
    apiClient
      .delete<
        ApiResponse<{ deleted_count?: number } | null>
      >(`/jobs/${id}`, { params: q })
      .then(r => r.data),

  assign: (id: string, body: AssignTechniciansRequest) =>
    apiClient
      .post<ApiResponse<Job>>(`/jobs/${id}/assign`, body)
      .then(r => r.data),

  reassign: (id: string, body: AssignTechniciansRequest) =>
    apiClient
      .put<ApiResponse<Job>>(`/jobs/${id}/reassign`, body)
      .then(r => r.data),

  start: (id: string) =>
    apiClient.post<ApiResponse<Job>>(`/jobs/${id}/start`).then(r => r.data),

  cancelStartup: (id: string) =>
    apiClient
      .post<ApiResponse<Job>>(`/jobs/${id}/cancel-startup`)
      .then(r => r.data),

  close: (id: string) =>
    apiClient.post<ApiResponse<Job>>(`/jobs/${id}/close`).then(r => r.data),

  cancel: (id: string, body: CancelJobRequest) =>
    apiClient
      .post<ApiResponse<Job>>(`/jobs/${id}/cancel`, body)
      .then(r => r.data),

  reschedule: (id: string, body: RescheduleJobRequest) =>
    apiClient
      .post<ApiResponse<Job>>(`/jobs/${id}/reschedule`, body)
      .then(r => r.data),

  // --- attachments (2.3.3) ---
  documents: (id: string, q: JobListQuery = {}) =>
    apiClient
      .get<ApiList<Documentable>>(`/jobs/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  uploadDocument: (id: string, file: File, docType?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (docType) form.append('doc_type', docType)
    return apiClient
      .post<ApiResponse<Documentable>>(`/jobs/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  // Attach an existing client/library file by id (select from library).
  linkDocument: (id: string, fileId: string, docType?: string) =>
    apiClient
      .post<ApiResponse<Documentable>>(`/jobs/${id}/documents/link`, {
        file_id: fileId,
        ...(docType ? { doc_type: docType } : {}),
      })
      .then(r => r.data),

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<Documentable>
      >(`/jobs/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data),

  deleteDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/jobs/${id}/documents/${documentId}`)
      .then(r => r.data),

  repeat: (id: string, body: RepeatScheduleRequest) =>
    apiClient
      .post<ApiResponse<RepeatSchedule>>(`/jobs/${id}/repeat`, body)
      .then(r => r.data),

  // --- reports (2.3.7 / 2.3.8 / 2.3.9) ---
  getReport: (id: string) =>
    apiClient
      .get<ApiResponse<JobReport>>(`/jobs/${id}/report`)
      .then(r => r.data),

  createReport: (id: string, body: JobReportRequest) =>
    apiClient
      .post<ApiResponse<JobReport>>(`/jobs/${id}/report`, body)
      .then(r => r.data),

  updateReport: (id: string, body: JobReportRequest) =>
    apiClient
      .put<ApiResponse<JobReport>>(`/jobs/${id}/report`, body)
      .then(r => r.data),

  // Per-service-category report option lists (Catalog 2.3.7 / 2.3.9). Passing
  // service_category narrows the response to that category's option groups.
  reportCatalog: (serviceCategory?: string) =>
    apiClient
      .get<ApiResponse<ReportCatalogResponse>>('/report-catalog', {
        params: serviceCategory
          ? { service_category: serviceCategory }
          : undefined,
      })
      .then(r => r.data),

  reportPdf: (id: string) =>
    apiClient
      .get<ApiResponse<PdfLink>>(`/jobs/${id}/report/pdf`, {
        timeout: 60000,
      })
      .then(r => r.data),

  sendReport: (id: string, body: SendDocumentRequest) =>
    apiClient
      .post<ApiResponse<JobReport>>(`/jobs/${id}/report/send`, body, {
        timeout: 60000,
      })
      .then(r => r.data),
}

export default jobsApi
